/**
 * Pure utility functions for parsing and building login/signup
 * origin context and redirect parameters.
 *
 * No side-effects, no hooks, no Firebase references.
 */

const ORIGIN_REASON_MAP = {
  JOIN_TEAM: 'join_team',
  CLAIM_TAG: 'claim_tag',
  WEBSITE: 'website',
};

function encodeOriginParams(params = {}) {
  try {
    const cleaned = Object.fromEntries(
      Object.entries(params || {}).filter(([, v]) => v != null)
    );
    if (Object.keys(cleaned).length === 0) return null;
    return JSON.stringify(cleaned);
  } catch (error) {
    return null;
  }
}

function decodeOriginParams(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch (error) { return {}; }
}

// Destination patterns per origin reason. '/client/join/' is the partner-integration
// handoff (xmid from the tipping page) — a team join in product terms, so it must get
// join_team origin (and the invite ask-first protection) too.
const JOIN_TEAM_REDIRECT_PATTERNS = ['/teams/', 'teaminvites', 'teaminvite', '/client/join/'];
const JOIN_TEAM_ROUTE_PATTERNS = ['teams', 'teaminvites', 'teaminvite', 'client/join'];
const CLAIM_REDIRECT_PATTERNS = ['/tags/', '/client/claim/'];
const CLAIM_ROUTE_PATTERNS = ['tags', 'claim'];

function textMatchesAny(text, patterns) {
  return patterns.some((pattern) => text.includes(pattern));
}

function inferOriginReason({ redirect, redirectRoute }) {
  const redirectText = `${redirect || ''}`.toLowerCase();
  const routeText = `${redirectRoute || ''}`.toLowerCase();
  const isJoinTeam = textMatchesAny(redirectText, JOIN_TEAM_REDIRECT_PATTERNS)
    || textMatchesAny(routeText, JOIN_TEAM_ROUTE_PATTERNS);
  if (isJoinTeam) return ORIGIN_REASON_MAP.JOIN_TEAM;
  const isClaim = textMatchesAny(redirectText, CLAIM_REDIRECT_PATTERNS)
    || textMatchesAny(routeText, CLAIM_ROUTE_PATTERNS);
  if (isClaim) return ORIGIN_REASON_MAP.CLAIM_TAG;
  return ORIGIN_REASON_MAP.WEBSITE;
}

function hasInboundOriginHints(queryParams = {}) {
  return !!(
    queryParams.redirect || queryParams.redirectRoute || queryParams.originReason
    || queryParams.originPath || queryParams.source || queryParams.tagId
    || queryParams.tagUid || queryParams.teamId || queryParams.qr || queryParams.uid
  );
}

function parseRedirectPathAndParams(redirect) {
  if (!redirect || typeof redirect !== 'string') return { path: null, params: {} };
  try {
    const normalized = redirect.startsWith('http://') || redirect.startsWith('https://')
      ? new URL(redirect) : new URL(redirect, window.location.origin);
    const params = {};
    normalized.searchParams.forEach((value, key) => { params[key] = value; });
    return { path: `${normalized.pathname || ''}${normalized.search || ''}` || null, params };
  } catch (error) {
    return { path: redirect, params: {} };
  }
}

// A claim link and a partner join link both name a TAG in the path. The join
// landing needs it to find the team (the tag doc carries teamId + teamName),
// since /client/join/{tagId}?xmid=… has no team id of its own.
function parseTagIdFromRedirect(redirect) {
  if (!redirect || typeof redirect !== 'string') return null;
  const match = redirect.match(/\/client\/(?:claim|join)\/([^/?#]+)/);
  return match ? match[1] : null;
}

function parseTeamIdFromRedirectRoute(redirectRoute) {
  if (!redirectRoute || typeof redirectRoute !== 'string') return null;
  const match = redirectRoute.match(/teamInvites\/([^/?#]+)/i);
  return match ? match[1] : null;
}

/** Extract teamId from a full path (e.g. redirect param) containing teamInvites/teamId/... */
function parseTeamIdFromRedirect(redirect) {
  if (!redirect || typeof redirect !== 'string') return null;
  const match = redirect.match(/teamInvites\/([^/?#]+)/i);
  return match ? match[1] : null;
}

function buildOriginContext(queryParams = {}, existingContext = null) {
  const inboundHinted = hasInboundOriginHints(queryParams);
  if (!inboundHinted && existingContext && existingContext.reason) return existingContext;
  const redirectInfo = parseRedirectPathAndParams(queryParams.redirect);
  const explicitReason = typeof queryParams.originReason === 'string'
    ? queryParams.originReason.trim().toLowerCase() : null;
  const reason = explicitReason || inferOriginReason(queryParams);
  const path = redirectInfo.path
    || (queryParams.redirectRoute ? `/client/manage/${queryParams.redirectRoute}` : '/client/login');
  return {
    reason, path,
    params: {
      redirect: queryParams.redirect || null,
      redirectRoute: queryParams.redirectRoute || null,
      source: queryParams.source || redirectInfo.params.source || null,
      tagId: queryParams.tagId || redirectInfo.params.tagId || parseTagIdFromRedirect(queryParams.redirect) || null,
      tagUid: queryParams.tagUid || redirectInfo.params.tagUid || null,
      teamId: queryParams.teamId || redirectInfo.params.teamId
        || parseTeamIdFromRedirectRoute(queryParams.redirectRoute)
        || parseTeamIdFromRedirect(redirectInfo.path || queryParams.redirect)
        || null,
      qr: queryParams.qr || redirectInfo.params.qr || null,
      uid: queryParams.uid || redirectInfo.params.uid || null,
    },
  };
}

function buildQueryString(params = {}) {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${v}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export {
  ORIGIN_REASON_MAP,
  encodeOriginParams,
  decodeOriginParams,
  inferOriginReason,
  buildOriginContext,
  buildQueryString,
};
